/**
 * Inventory Packages — pure logic (PRD.260).
 *
 * Dependency-free helpers for package validation and value math. Kept import-
 * free on purpose so they can be unit-tested with Node's built-in test runner
 * (see inventory-packages-logic.test.ts) and reused by both the web-admin UI
 * and any future server-side validation.
 */

/** A minimal shape for value math — one included child item. */
export interface PackageValueItem {
  /** The child's usual (current) selling price. */
  usualPrice: number;
  /** How many of the child the package includes. */
  quantity: number;
}

/**
 * Validity period rule (PRD.260 §2, §12): must be null (no validity) or a
 * whole number greater than 0. Non-integer or <= 0 values are invalid.
 */
export function isValidValidityMonths(value: number | null | undefined): boolean {
  if (value === null || value === undefined) return true;
  return Number.isInteger(value) && value > 0;
}

/** Quantity rule (PRD.260 §4, §12): must be a finite number greater than 0. */
export function isValidPackageQuantity(qty: number): boolean {
  return Number.isFinite(qty) && qty > 0;
}

/**
 * Regular total value (PRD.260 §7): sum(child usual price * quantity).
 * Non-finite prices/quantities contribute 0.
 */
export function computeRegularTotalValue(items: readonly PackageValueItem[]): number {
  return items.reduce((sum, i) => {
    const price = Number.isFinite(i.usualPrice) ? i.usualPrice : 0;
    const qty = Number.isFinite(i.quantity) ? i.quantity : 0;
    return sum + price * qty;
  }, 0);
}

/**
 * Patient savings (PRD.260 §7): Regular Total Value - Package Price. A
 * negative result means the package costs more than its parts (the UI shows
 * this as a "Price difference" rather than negative savings).
 */
export function computePatientSavings(regularTotal: number, packagePrice: number): number {
  return regularTotal - packagePrice;
}

/**
 * Composition rule check (PRD.260 §4, §12; PRD.267 §16). Returns a user-facing
 * error string when the child cannot be added, or null when it is allowed.
 * Cross-org items are excluded at the query layer, so they are not re-checked
 * here.
 *
 * PRD.267 — a child is either an inventory item or an appointment service.
 * - Inventory children keep the original rules (no self, no nested package,
 *   no duplicate inventory child).
 * - Appointment-service children can never be the package itself or a package,
 *   so only the duplicate check applies, against the separate service id set.
 *
 * `childKind` defaults to 'inventory' and `existingServiceIds` to [] so existing
 * inventory-only callers/tests are unaffected.
 */
export function validatePackageChild(input: {
  childId: string;
  childItemType: string | null;
  packageId: string;
  existingChildIds: readonly string[];
  childKind?: 'inventory' | 'service';
  existingServiceIds?: readonly string[];
}): string | null {
  const {
    childId,
    childItemType,
    packageId,
    existingChildIds,
    childKind = 'inventory',
    existingServiceIds = [],
  } = input;

  if (childKind === 'service') {
    if (existingServiceIds.includes(childId)) {
      return 'This service is already included in the package.';
    }
    return null;
  }

  if (childId === packageId) {
    return 'A package cannot include itself.';
  }
  if ((childItemType ?? '').toLowerCase() === 'package') {
    return 'Packages cannot be added inside another package.';
  }
  if (existingChildIds.includes(childId)) {
    return 'This item is already included in the package.';
  }
  return null;
}
